import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  Card,
  CardHeader,
  AlertBox,
  formatSquadInfo,
} from "../UI/DesignStandards";
import { PrimaryButton, SecondaryButton } from "../UI/DesignStandards/Buttons";
import { FormField } from "../UI/DesignStandards/Forms";
import { ToggleSwitch } from "../UI/DesignStandards/Toggles";
import SignupReviewSkeleton from "./SignupReview/SignupReviewSkeleton";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";
import {
  FaTrophy,
  FaUser,
  FaDollarSign,
  FaArrowLeft,
  FaSave,
} from "react-icons/fa";

const SignupEdit = () => {
  const { signupId } = useParams();
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [signupData, setSignupData] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [event, setEvent] = useState(null);
  const [squad, setSquad] = useState(null);
  const [signupSettings, setSignupSettings] = useState(null);
  const [bowlers, setBowlers] = useState([]);
  const [selectedSidepots, setSelectedSidepots] = useState({});
  const [sidepotEvents, setSidepotEvents] = useState([]);

  // Load signup data on component mount
  useEffect(() => {
    let isMounted = true;

    if (signupId) {
      loadSignupData().then(() => {
        if (!isMounted) {
          return;
        }
      });
    }

    return () => {
      isMounted = false;
    };
  }, [signupId]);

  const loadSignupData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use edge function to get signup data
      const { data, error } = await supabase.functions.invoke(
        "get-signup-data",
        {
          body: { signupId },
        }
      );

      if (error) {
        setError("Failed to load signup data. Please try again.");
        return;
      }

      if (data.error) {
        setError(data.error);
        return;
      }

      // Set all the data from the edge function response
      setSignupData(data.signup);
      setEvent(data.signup.tournamentEvents);
      setTournament(data.signup.tournamentEvents.tournaments);
      setSignupSettings(data.signupSettings);

      // Set bowlers data
      setBowlers(data.bowlers || []);
      setSidepotEvents(data.sidepots || []);

      let squadRow = data.squad || null;
      if (!squadRow && data.signup.squadID) {
        const { data: loadedSquad } = await supabase
          .from("squads")
          .select("squadDate, squadTime, description")
          .eq("id", data.signup.squadID)
          .maybeSingle();
        squadRow = loadedSquad;
      }
      setSquad(squadRow);

      // Load existing sidepot selections
      if (data.bowlers && data.sidepots) {
        const initialSidepots = {};

        // Initialize all sidepots as unselected first
        for (const bowler of data.bowlers) {
          initialSidepots[bowler.id] = {};
          for (const sidepot of data.sidepots) {
            initialSidepots[bowler.id][sidepot.id] = false;
          }
        }

        // Mark existing selections as selected
        if (data.existingSidepots) {
          for (const selection of data.existingSidepots) {
            const bowler = data.bowlers.find(
              (b) => b.id === selection.tournamentSignupBowlerID
            );
            if (bowler && initialSidepots[bowler.id]) {
              initialSidepots[bowler.id][selection.eventID] = true;
            }
          }
        }

        setSelectedSidepots(initialSidepots);
      }
    } catch (error) {
      console.error("Error in loadSignupData:", error);
      setError("Failed to load signup data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBowlerUpdate = (index, field, value) => {
    const updatedBowlers = [...bowlers];
    updatedBowlers[index] = {
      ...updatedBowlers[index],
      [field]: value,
    };
    setBowlers(updatedBowlers);
  };

  // Helper function to validate average range
  const isValidAverage = (average) => {
    if (!average || average === "") return true; // Allow empty for required validation
    const avg = parseFloat(average);
    return !isNaN(avg) && avg >= 0 && avg <= 300;
  };

  const handleSidepotToggle = (bowlerId, sidepotId, selected) => {
    setSelectedSidepots((prev) => ({
      ...prev,
      [bowlerId]: {
        ...prev[bowlerId],
        [sidepotId]: selected,
      },
    }));
  };

  const validateForm = () => {
    const errors = [];

    // Validate primary bowler
    const primaryBowler = bowlers.find((b) => b.role === "primary");
    if (!primaryBowler?.name?.trim()) {
      errors.push("Primary bowler name is required");
    }
    if (signupSettings?.requireEmail && !primaryBowler?.email?.trim()) {
      errors.push("Primary bowler email is required");
    }
    if (!primaryBowler?.average || primaryBowler.average === "") {
      errors.push("Primary bowler average is required");
    } else {
      const avg = parseFloat(primaryBowler.average);
      if (isNaN(avg) || avg < 0 || avg > 300) {
        errors.push("Primary bowler average must be between 0 and 300");
      }
    }

    // Validate teammates
    const teammates = bowlers.filter((b) => b.role === "teammate");
    teammates.forEach((teammate, index) => {
      if (!teammate?.name?.trim()) {
        errors.push(`Teammate ${index + 1} name is required`);
      }
      if (signupSettings?.requireEmail && !teammate?.email?.trim()) {
        errors.push(`Teammate ${index + 1} email is required`);
      }
      if (!teammate?.average || teammate.average === "") {
        errors.push(`Teammate ${index + 1} average is required`);
      } else {
        const avg = parseFloat(teammate.average);
        if (isNaN(avg) || avg < 0 || avg > 300) {
          errors.push(
            `Teammate ${index + 1} average must be between 0 and 300`
          );
        }
      }
    });

    return errors;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        setError(validationErrors.join(", "));
        return;
      }

      // Clean the bowlers data to remove any circular references
      const cleanBowlers = bowlers.map((bowler) => ({
        id: bowler.id,
        name: bowler.name,
        email: bowler.email,
        phone: bowler.phone,
        usbcID: bowler.usbcID,
        average: bowler.average,
        role: bowler.role,
        matchedBowlerId: bowler.matchedBowlerId,
        signupID: bowler.signupID,
      }));

      // Clean the selectedSidepots data to remove any circular references
      const cleanSelectedSidepots = {};
      Object.keys(selectedSidepots).forEach((bowlerId) => {
        cleanSelectedSidepots[bowlerId] = {};
        Object.keys(selectedSidepots[bowlerId]).forEach((sidepotId) => {
          const value = selectedSidepots[bowlerId][sidepotId];
          // Only copy primitive values (boolean, string, number)
          if (
            typeof value === "boolean" ||
            typeof value === "string" ||
            typeof value === "number"
          ) {
            cleanSelectedSidepots[bowlerId][sidepotId] = value;
          } else {
            console.warn(
              `Skipping non-primitive value for ${bowlerId}.${sidepotId}:`,
              value
            );
            cleanSelectedSidepots[bowlerId][sidepotId] = false; // Default to false
          }
        });
      });

      const { data, error } = await supabase.functions.invoke(
        "update-signup-admin",
        {
          body: {
            signupId: signupId,
            updates: {
              bowlers: cleanBowlers,
              selectedSidepots: cleanSelectedSidepots,
            },
          },
        }
      );

      if (error) {
        setError("Failed to save changes. Please try again.");
        return;
      }

      if (data.error) {
        setError(data.error);
        return;
      }

      // Show success notification and navigate to dashboard
      notify.success("Signup saved.");
      navigate(tournamentPath(tournamentSlug, `/signup-dashboard`));
    } catch (error) {
      console.error("Error in handleSave:", error);
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatSquadDateTime = () => {
    if (!squad) {
      if (signupData?.squadID) {
        return "Loading squad info...";
      }
      return "N/A";
    }

    // Format the squad date and time using design standards
    return formatSquadInfo(squad.squadDate, squad.squadTime);
  };

  if (loading) {
    return <SignupReviewSkeleton />;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <AlertBox type="error" title="Error" message={error} />
        <div className="mt-4">
          <SecondaryButton
            onClick={() =>
              navigate(tournamentPath(tournamentSlug, `/signup-dashboard`))
            }
            className="flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </SecondaryButton>
        </div>
      </div>
    );
  }

  if (!signupData || !tournament || !event) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <AlertBox
          type="error"
          title="Error"
          message="Signup not found or no longer available for editing."
        />
        <div className="mt-4">
          <SecondaryButton
            onClick={() =>
              navigate(tournamentPath(tournamentSlug, `/signup-dashboard`))
            }
            className="flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </SecondaryButton>
        </div>
      </div>
    );
  }

  // Show success state
  if (successMessage) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">
          <div className="mb-8">
            <div className="w-16 h-16 bg-brand-charcoal rounded-lg flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-brand-cream"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Success!</h1>
            <p className="text-xl text-gray-600">{successMessage}</p>
          </div>

          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <SecondaryButton
                onClick={() =>
                  navigate(tournamentPath(tournamentSlug, `/signup-dashboard`))
                }
                className="flex items-center gap-2"
              >
                <FaTrophy className="w-4 h-4" />
                Back to Dashboard
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const primaryBowler = bowlers.find((b) => b.role === "primary");
  const teammates = bowlers.filter((b) => b.role === "teammate");

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <SecondaryButton
            onClick={() =>
              navigate(
                tournamentPath(tournamentSlug, `/signup-dashboard/${signupId}`)
              )
            }
            className="flex items-center gap-2"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back to Signup
          </SecondaryButton>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Signup</h1>
            <p className="text-gray-600">
              Change bowlers, averages, and sidepots.
            </p>
          </div>
        </div>
        <PrimaryButton
          onClick={handleSave}
          disabled={saving}
          loading={saving ? true : undefined}
          className="flex items-center gap-2"
        >
          <FaSave className="w-4 h-4" />
          Save
        </PrimaryButton>
      </div>

      {/* Tournament Info */}
      <Card className="mb-6">
        <CardHeader title="Tournament Information" />
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Tournament:</span>
              <span className="ml-2 text-gray-900">{tournament.name}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Event:</span>
              <span className="ml-2 text-gray-900">{event.eventName}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Squad:</span>
              <span className="ml-2 text-gray-900">
                {formatSquadDateTime()}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Status:</span>
              <span className="ml-2 text-gray-900">{signupData.status}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Primary Bowler */}
      <Card className="mb-6">
        <CardHeader title="Primary Bowler" />
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Name"
              type="text"
              value={primaryBowler?.name || ""}
              onChange={(e) => handleBowlerUpdate(0, "name", e.target.value)}
              required
              placeholder="Enter bowler name"
            />
            <FormField
              label="Email"
              type="email"
              value={primaryBowler?.email || ""}
              onChange={(e) => handleBowlerUpdate(0, "email", e.target.value)}
              required
              placeholder="Enter email address"
            />
            <FormField
              label="Phone"
              type="tel"
              value={primaryBowler?.phone || ""}
              onChange={(e) => handleBowlerUpdate(0, "phone", e.target.value)}
              required={signupSettings?.requirePhone}
              placeholder="Enter phone number"
            />
            <FormField
              label="USBC ID"
              type="text"
              value={primaryBowler?.usbcID || ""}
              onChange={(e) => handleBowlerUpdate(0, "usbcID", e.target.value)}
              required={signupSettings?.requireUSBCID}
              placeholder="Enter USBC ID"
            />
            <FormField
              label="Average *"
              type="number"
              value={primaryBowler?.average || ""}
              onChange={(e) => handleBowlerUpdate(0, "average", e.target.value)}
              required
              step="0.1"
              min="0"
              max="300"
              placeholder="Enter bowling average (will be rounded to nearest integer)"
              className={
                !primaryBowler?.average ||
                !isValidAverage(primaryBowler?.average)
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }
            />
            {primaryBowler?.average &&
              !isValidAverage(primaryBowler.average) && (
                <div className="text-red-600 text-sm mt-1">
                  Average must be between 0 and 300
                </div>
              )}
          </div>
        </div>
      </Card>

      {/* Teammates */}
      {teammates.length > 0 && (
        <Card className="mb-6">
          <CardHeader title="Teammates" />
          <div className="p-6">
            {teammates.map((teammate, index) => (
              <div key={teammate.id} className="mb-6 last:mb-0">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">
                  Teammate {index + 1}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="Name"
                    type="text"
                    value={teammate?.name || ""}
                    onChange={(e) =>
                      handleBowlerUpdate(index + 1, "name", e.target.value)
                    }
                    required
                    placeholder="Enter bowler name"
                  />
                  <FormField
                    label="Email"
                    type="email"
                    value={teammate?.email || ""}
                    onChange={(e) =>
                      handleBowlerUpdate(index + 1, "email", e.target.value)
                    }
                    required
                    placeholder="Enter email address"
                  />
                  <FormField
                    label="Phone"
                    type="tel"
                    value={teammate?.phone || ""}
                    onChange={(e) =>
                      handleBowlerUpdate(index + 1, "phone", e.target.value)
                    }
                    required={signupSettings?.requirePhone}
                    placeholder="Enter phone number"
                  />
                  <FormField
                    label="USBC ID"
                    type="text"
                    value={teammate?.usbcID || ""}
                    onChange={(e) =>
                      handleBowlerUpdate(index + 1, "usbcID", e.target.value)
                    }
                    required={signupSettings?.requireUSBCID}
                    placeholder="Enter USBC ID"
                  />
                  <FormField
                    label="Average *"
                    type="number"
                    value={teammate?.average || ""}
                    onChange={(e) =>
                      handleBowlerUpdate(index + 1, "average", e.target.value)
                    }
                    required
                    step="0.1"
                    min="0"
                    max="300"
                    placeholder="Enter bowling average (will be rounded to nearest integer)"
                    className={
                      !teammate?.average || !isValidAverage(teammate?.average)
                        ? "border-red-300 focus:border-red-500"
                        : ""
                    }
                  />
                  {teammate?.average && !isValidAverage(teammate.average) && (
                    <div className="text-red-600 text-sm mt-1">
                      Average must be between 0 and 300
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sidepot Events */}
      {sidepotEvents.length > 0 ? (
        <Card className="mb-6">
          <CardHeader title="Select Sidepots by Bowler" icon={FaTrophy} />
          <div className="p-6">
            <div className="space-y-6">
              {bowlers.map((bowler, index) => (
                <div
                  key={bowler.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <FaUser className="w-5 h-5 text-brand-muted mr-3" />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {bowler.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {bowler.role === "primary"
                            ? "Primary Bowler"
                            : "Teammate"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {
                          Object.values(
                            selectedSidepots[bowler.id] || {}
                          ).filter(Boolean).length
                        }{" "}
                        selected
                      </div>
                      <div className="text-sm font-medium text-brand-charcoal">
                        $
                        {sidepotEvents.reduce((total, sidepot) => {
                          const isSelected =
                            selectedSidepots[bowler.id]?.[sidepot.id] || false;
                          return (
                            total + (isSelected ? sidepot.entryFee || 0 : 0)
                          );
                        }, 0)}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3">
                    {sidepotEvents.map((sidepot) => (
                      <div
                        key={sidepot.id}
                        className="flex items-center justify-between p-3 bg-brand-cream rounded-lg"
                      >
                        <div className="flex items-center">
                          <ToggleSwitch
                            checked={
                              selectedSidepots[bowler.id]?.[sidepot.id] || false
                            }
                            onChange={(checked) =>
                              handleSidepotToggle(
                                bowler.id,
                                sidepot.id,
                                checked
                              )
                            }
                            className="mr-3"
                          />
                          <div>
                            <div className="font-medium text-gray-900">
                              {sidepot.eventName}
                            </div>
                            <div className="text-sm text-gray-600">
                              {sidepot.eventDescription}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-brand-charcoal">
                            ${sidepot.entryFee || 0}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      ) : (
        <Card className="mb-6">
          <CardHeader title="Sidepot Events" />
          <div className="p-6">
            <p className="text-gray-600">
              No sidepot events available for this tournament.
            </p>
          </div>
        </Card>
      )}

      {/* Cost Summary */}
      <Card className="mb-6">
        <CardHeader title="Cost Summary" icon={FaDollarSign} />
        <div className="p-6">
          <div className="space-y-4">
            {/* Primary Event Cost */}
            <div className="flex justify-between items-center p-3 bg-brand-cream rounded-lg border border-brand-charcoal/10">
              <div className="flex items-center">
                <FaTrophy className="w-4 h-4 text-brand-gold-ink mr-2" />
                <span className="font-medium text-gray-900">
                  Primary Event: {event.eventName}
                </span>
                <span className="text-sm text-gray-500 ml-2">
                  ({bowlers.length} bowler{bowlers.length !== 1 ? "s" : ""} × $
                  {event.entryFee || 0} each)
                </span>
              </div>
              <div className="text-right">
                <div className="font-semibold text-brand-gold-ink">
                  ${(event.entryFee || 0) * bowlers.length}
                </div>
              </div>
            </div>

            {/* Sidepot Costs */}
            {sidepotEvents.length > 0 && (
              <>
                {bowlers.map((bowler) => {
                  const bowlerSidepotTotal = sidepotEvents.reduce(
                    (total, sidepot) => {
                      const isSelected =
                        selectedSidepots[bowler.id]?.[sidepot.id] || false;
                      return total + (isSelected ? sidepot.entryFee || 0 : 0);
                    },
                    0
                  );

                  const selectedCount = Object.values(
                    selectedSidepots[bowler.id] || {}
                  ).filter(Boolean).length;

                  return (
                    <div
                      key={bowler.id}
                      className="flex justify-between items-center p-3 bg-brand-cream rounded-lg"
                    >
                      <div className="flex items-center">
                        <FaUser className="w-4 h-4 text-brand-muted mr-2" />
                        <span className="font-medium text-gray-900">
                          {bowler.name} - Sidepots
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({selectedCount} sidepot
                          {selectedCount !== 1 ? "s" : ""})
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-brand-charcoal">
                          ${bowlerSidepotTotal}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">
                      Total Sidepot Cost
                    </span>
                    <span className="font-bold text-lg text-brand-charcoal">
                      $
                      {bowlers.reduce((total, bowler) => {
                        return (
                          total +
                          sidepotEvents.reduce((bowlerTotal, sidepot) => {
                            const isSelected =
                              selectedSidepots[bowler.id]?.[sidepot.id] ||
                              false;
                            return (
                              bowlerTotal +
                              (isSelected ? sidepot.entryFee || 0 : 0)
                            );
                          }, 0)
                        );
                      }, 0)}
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Total Cost */}
            <div className="border-t-2 border-gray-300 pt-4">
              <div className="flex justify-between items-center">
                <span className="font-bold text-lg text-gray-900">
                  Total Cost
                </span>
                <span className="font-bold text-xl text-brand-gold-ink">
                  $
                  {(event.entryFee || 0) * bowlers.length +
                    bowlers.reduce((total, bowler) => {
                      return (
                        total +
                        sidepotEvents.reduce((bowlerTotal, sidepot) => {
                          const isSelected =
                            selectedSidepots[bowler.id]?.[sidepot.id] || false;
                          return (
                            bowlerTotal +
                            (isSelected ? sidepot.entryFee || 0 : 0)
                          );
                        }, 0)
                      );
                    }, 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SignupEdit;
